import React, { useState } from 'react';
import axios from 'axios';

const RollChecker = () => {
  const [giveawayId, setGiveawayId] = useState('');
  const [verificationData, setVerificationData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setVerificationData(null);
    setLoading(true);

    try {
      const response = await axios.get(`/api/giveaways/verify/${giveawayId.trim()}`);
      setVerificationData(response.data);
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.message || 'Failed to verify giveaway. Please check the ID and try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div
        style={{
          backgroundColor: '#2d2d2d',
          padding: '30px',
          borderRadius: '12px',
          marginBottom: '30px',
          textAlign: 'center'
        }}
      >
        <h2 style={{ margin: '0 0 15px 0', fontSize: '2rem', color: '#ffffff' }}>🔍 Roll Checker</h2>
        <p style={{ margin: '0 0 20px 0', color: '#b0b0b0', fontSize: '1rem' }}>
          Verify the fairness of any giveaway by entering its unique ID below.
          This system ensures transparency and prevents cheating.
        </p>

        <form onSubmit={handleVerify} style={{ maxWidth: '500px', margin: '0 auto' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input
              type="text"
              value={giveawayId}
              onChange={(e) => setGiveawayId(e.target.value)}
              placeholder="Enter Giveaway ID (e.g., GU-A1B2C3)"
              style={{
                flex: 1,
                padding: '15px',
                borderRadius: '8px',
                border: '2px solid #2196F3',
                fontSize: '1rem',
                fontFamily: 'monospace',
                backgroundColor: '#1a1a1a',
                color: '#ffffff'
              }}
              required
            />
            <button
              type="submit"
              disabled={loading || !giveawayId.trim()}
              style={{
                padding: '15px 30px',
                backgroundColor: loading || !giveawayId.trim() ? '#555' : '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading || !giveawayId.trim() ? 'not-allowed' : 'pointer',
                fontSize: '1rem',
                fontWeight: 'bold',
                transition: 'background-color 0.3s'
              }}
            >
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </div>
        </form>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#3d1f1f',
            color: '#ff6b6b',
            padding: '20px',
            borderRadius: '12px',
            marginBottom: '30px',
            textAlign: 'center'
          }}
        >
          <p style={{ margin: 0, fontSize: '1rem' }}>{error}</p>
        </div>
      )}

      {verificationData && (
        <div>
          {/* Header Card */}
          <div
            style={{
              backgroundColor: '#2d2d2d',
              borderRadius: '12px',
              padding: '30px',
              marginBottom: '20px',
              boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
              border: '3px solid #4CAF50'
            }}
          >
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '3rem', marginBottom: '10px' }}>✅</div>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '1.8rem', color: '#4CAF50' }}>
                Giveaway Verified
              </h3>
              <p style={{ margin: 0, color: '#b0b0b0' }}>This giveaway has been verified as legitimate</p>
            </div>

            {/* Giveaway ID */}
            <div
              style={{
                backgroundColor: '#1a1a1a',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center'
              }}
            >
              <p style={{ margin: '0 0 5px 0', fontSize: '0.85rem', color: '#888' }}>Giveaway ID</p>
              <p
                style={{
                  margin: 0,
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  fontFamily: 'monospace',
                  color: '#ffffff'
                }}
              >
                {verificationData.giveawayId}
              </p>
            </div>

            {/* Basic Info */}
            <div style={{ marginBottom: '20px' }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '1.3rem', color: '#ffffff' }}>📋 Giveaway Information</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '12px', fontSize: '1rem' }}>
                <strong style={{ color: '#b0b0b0' }}>Title:</strong>
                <span style={{ color: '#ffffff' }}>{verificationData.title}</span>
                
                <strong style={{ color: '#b0b0b0' }}>Host:</strong>
                <span style={{ color: '#ffffff' }}>{verificationData.host}</span>
                
                <strong style={{ color: '#b0b0b0' }}>Status:</strong>
                <span
                  style={{
                    display: 'inline-block',
                    backgroundColor:
                      verificationData.status === 'rolled'
                        ? '#9C27B0'
                        : verificationData.status === 'ongoing'
                        ? '#4CAF50'
                        : '#FF9800',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold'
                  }}
                >
                  {verificationData.status.toUpperCase()}
                </span>
                
                <strong style={{ color: '#b0b0b0' }}>End Date:</strong>
                <span style={{ color: '#ffffff' }}>{formatDate(verificationData.endDate)}</span>
                
                <strong style={{ color: '#b0b0b0' }}>Total Participants:</strong>
                <span style={{ fontWeight: 'bold', color: '#2196F3' }}>
                  {verificationData.totalParticipants}
                </span>
              </div>
            </div>

            {/* Current Winner */}
            {verificationData.currentWinner && (
              <div
                style={{
                  backgroundColor: '#1a3a1a',
                  padding: '20px',
                  borderRadius: '12px',
                  textAlign: 'center',
                  border: '2px solid #4CAF50'
                }}
              >
                <h4 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: '#66BB6A' }}>
                  🏆 Current Winner
                </h4>
                <p style={{ margin: 0, fontSize: '1.8rem', fontWeight: 'bold', color: '#81C784' }}>
                  {verificationData.currentWinner}
                </p>
              </div>
            )}
          </div>

          {/* Roll History */}
          {verificationData.rollLogs && verificationData.rollLogs.length > 0 && (
            <div
              style={{
                backgroundColor: '#2d2d2d',
                borderRadius: '12px',
                padding: '30px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
              }}
            >
              <h4 style={{ margin: '0 0 20px 0', fontSize: '1.5rem', color: '#ffffff' }}>📜 Roll History</h4>
              <p style={{ margin: '0 0 20px 0', color: '#b0b0b0', fontSize: '0.95rem' }}>
                Complete history of all winner rolls for this giveaway. Each entry is logged with a timestamp
                to ensure transparency and fairness.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {verificationData.rollLogs.map((log, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: '#1a1a1a',
                      padding: '20px',
                      borderRadius: '12px',
                      border: index === verificationData.rollLogs.length - 1 ? '2px solid #4CAF50' : '1px solid #444',
                      position: 'relative'
                    }}
                  >
                    {/* Roll Number Badge */}
                    <div
                      style={{
                        position: 'absolute',
                        top: '-12px',
                        left: '15px',
                        backgroundColor: index === verificationData.rollLogs.length - 1 ? '#4CAF50' : '#2196F3',
                        color: 'white',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: 'bold'
                      }}
                    >
                      {log.isReroll ? `🔄 Reroll #${index}` : index === 0 ? '🎲 Initial Roll' : `Roll #${index + 1}`}
                    </div>

                    {index === verificationData.rollLogs.length - 1 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '-12px',
                          right: '15px',
                          backgroundColor: '#FF9800',
                          color: 'white',
                          padding: '6px 14px',
                          borderRadius: '20px',
                          fontSize: '0.85rem',
                          fontWeight: 'bold'
                        }}
                      >
                        ⭐ Latest
                      </div>
                    )}

                    <div style={{ marginTop: '15px', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '12px' }}>
                      <strong style={{ color: '#b0b0b0' }}>Winner:</strong>
                      <span style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#ffffff' }}>
                        {log.winner}
                      </span>

                      <strong style={{ color: '#b0b0b0' }}>Roll Method:</strong>
                      <span style={{ color: '#ffffff' }}>
                        {log.rollType === 'spin_wheel' ? '🎡 Spin the Wheel' : '⚡ Normal Roll'}
                      </span>

                      <strong style={{ color: '#b0b0b0' }}>Participants:</strong>
                      <span style={{ color: '#ffffff' }}>{log.totalParticipants}</span>

                      <strong style={{ color: '#b0b0b0' }}>Timestamp:</strong>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#ffffff' }}>
                        {formatDate(log.timestamp)}
                      </span>

                      {log.isReroll && log.rerollReason && (
                        <>
                          <strong style={{ color: '#b0b0b0' }}>Reroll Reason:</strong>
                          <span style={{ color: '#888', fontStyle: 'italic' }}>
                            {log.rerollReason}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div
                style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#1a2a3a',
                  borderRadius: '8px'
                }}
              >
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#64B5F6' }}>
                  <strong>Total Rolls:</strong> {verificationData.rollLogs.length} •{' '}
                  <strong>Rerolls:</strong>{' '}
                  {verificationData.rollLogs.filter((log) => log.isReroll).length}
                </p>
              </div>
            </div>
          )}

          {verificationData.rollLogs && verificationData.rollLogs.length === 0 && (
            <div
              style={{
                backgroundColor: '#2d2d2d',
                borderRadius: '12px',
                padding: '40px',
                textAlign: 'center',
                boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
              }}
            >
              <div style={{ fontSize: '3rem', marginBottom: '15px' }}>⏳</div>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '1.3rem', color: '#b0b0b0' }}>
                No Winner Rolled Yet
              </h4>
              <p style={{ margin: 0, color: '#888' }}>
                The host hasn't rolled a winner for this giveaway yet.
                {verificationData.status === 'ongoing' && ' The giveaway is still ongoing.'}
              </p>
            </div>
          )}

          {/* Anti-Cheat Info */}
          <div
            style={{
              backgroundColor: '#2d1a3d',
              padding: '20px',
              borderRadius: '12px',
              marginTop: '20px',
              border: '1px solid #9C27B0'
            }}
          >
            <h4 style={{ margin: '0 0 10px 0', fontSize: '1.1rem', color: '#BA68C8' }}>
              🛡️ Anti-Cheat Verification
            </h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#b0b0b0', lineHeight: '1.6' }}>
              All roll data is stored securely in our database and cannot be modified. The unique Giveaway ID
              ensures that this verification is authentic. Each roll is timestamped and logged with complete
              participant information to maintain fairness and transparency.
            </p>
          </div>
        </div>
      )}

      {!verificationData && !error && !loading && (
        <div
          style={{
            backgroundColor: '#2d2d2d',
            borderRadius: '12px',
            padding: '60px 30px',
            textAlign: 'center',
            boxShadow: '0 4px 6px rgba(0,0,0,0.3)'
          }}
        >
          <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🔍</div>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '1.5rem', color: '#ffffff' }}>
            Enter a Giveaway ID to verify
          </h3>
          <p style={{ margin: 0, color: '#b0b0b0' }}>
            You can find the Giveaway ID on any giveaway card or in the giveaway details
          </p>
        </div>
      )}
    </div>
  );
};

export default RollChecker;
